import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PlusIcon,
  EyeIcon,
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
  CreditCardIcon,
} from '@heroicons/react/24/outline';
import accountService from '../../services/accountService';
import { useToast } from '../../contexts/ToastContext';

const SavingsAccounts = () => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [transactionAmount, setTransactionAmount] = useState('');
  
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();

  useEffect(() => {
    fetchAccounts();
  }, []);

  const fetchAccounts = async () => {
    try {
      setLoading(true);
      const data = await accountService.getAllAccounts();
      setAccounts(data.accounts || []);
    } catch (error) {
      showError('Erreur lors du chargement des comptes');
    } finally {
      setLoading(false);
    }
  };

  const filteredAccounts = accounts.filter(account =>
    account.account_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
    account.client?.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    account.client?.last_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleDeposit = async () => {
    try {
      await accountService.deposit(selectedAccount.id, {
        amount: parseFloat(transactionAmount),
        description: 'Dépôt manuel'
      });
      showSuccess('Dépôt effectué avec succès');
      setShowDepositModal(false);
      setTransactionAmount('');
      fetchAccounts();
    } catch (error) {
      showError('Erreur lors du dépôt');
    }
  };

  const handleWithdraw = async () => {
    try {
      await accountService.withdraw(selectedAccount.id, {
        amount: parseFloat(transactionAmount),
        description: 'Retrait manuel'
      });
      showSuccess('Retrait effectué avec succès');
      setShowWithdrawModal(false);
      setTransactionAmount('');
      fetchAccounts();
    } catch (error) {
      showError('Erreur lors du retrait');
    }
  };

  const getStatusBadge = (status) => {
    const configs = {
      active: { label: 'Actif', color: 'badge-success' },
      dormant: { label: 'Dormant', color: 'badge-warning' },
      closed: { label: 'Fermé', color: 'badge-danger' },
    };
    const config = configs[status] || { label: status, color: 'badge-info' };
    return <span className={`badge ${config.color}`}>{config.label}</span>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <svg className="animate-spin h-8 w-8 text-primary-600 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="mt-2 text-sm text-gray-600">Chargement des comptes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Comptes d'Épargne</h1>
          <p className="mt-1 text-sm text-gray-600">
            Gestion des comptes d'épargne clients
          </p>
        </div>
        <button
          onClick={() => navigate('/accounts/new')}
          className="btn-primary"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Nouveau Compte
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
        <div className="card">
          <div className="p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0 rounded-md bg-green-100 p-3">
                <CreditCardIcon className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Épargne</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {accounts.reduce((sum, acc) => sum + (acc.balance || 0), 0).toLocaleString('fr-FR')} HTG
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0 rounded-md bg-blue-100 p-3">
                <CreditCardIcon className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Comptes Actifs</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {accounts.filter(a => a.status === 'active').length}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0 rounded-md bg-yellow-100 p-3">
                <CreditCardIcon className="h-6 w-6 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Comptes Dormants</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {accounts.filter(a => a.status === 'dormant').length}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="p-6">
          <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4">
            <div className="flex-1">
              <input
                type="text"
                className="input-field"
                placeholder="Rechercher par numéro de compte ou nom du client"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="flex space-x-3">
              <select className="input-field w-full sm:w-auto">
                <option value="all">Tous les statuts</option>
                <option value="active">Actif</option>
                <option value="dormant">Dormant</option>
                <option value="closed">Fermé</option>
              </select>
              <select className="input-field w-full sm:w-auto">
                <option value="all">Tous les types</option>
                <option value="regular">Régulier</option>
                <option value="fixed">À terme</option>
                <option value="youth">Jeunesse</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Accounts Table */}
      <div className="card">
        <div className="px-4 py-5 sm:p-6">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    N° Compte
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Client
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Solde
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Taux d'intérêt
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredAccounts.map((account) => (
                  <tr key={account.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {account.account_number}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {account.client?.first_name} {account.client?.last_name}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {(account.balance || 0).toLocaleString('fr-FR')} HTG
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {account.interest_rate}%
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 capitalize">
                        {account.account_type}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(account.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => navigate(`/accounts/${account.id}`)}
                          className="text-primary-600 hover:text-primary-900"
                          title="Voir détails"
                        >
                          <EyeIcon className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => {
                            setSelectedAccount(account);
                            setShowDepositModal(true);
                          }}
                          className="text-green-600 hover:text-green-900"
                          title="Déposer"
                        >
                          <ArrowDownTrayIcon className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => {
                            setSelectedAccount(account);
                            setShowWithdrawModal(true);
                          }}
                          className="text-yellow-600 hover:text-yellow-900"
                          title="Retirer"
                        >
                          <ArrowUpTrayIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {filteredAccounts.length === 0 && (
              <div className="text-center py-12">
                <CreditCardIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">Aucun compte trouvé</h3>
                <p className="mt-1 text-sm text-gray-500">
                  {searchTerm
                    ? 'Essayez de modifier vos critères de recherche.'
                    : 'Commencez par créer votre premier compte.'}
                </p>
                {!searchTerm && (
                  <div className="mt-6">
                    <button
                      onClick={() => navigate('/accounts/new')}
                      className="btn-primary"
                    >
                      <PlusIcon className="h-5 w-5 mr-2" />
                      Créer un compte
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Deposit Modal */}
      {showDepositModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Dépôt sur le compte {selectedAccount?.account_number}
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Montant (HTG)
                  </label>
                  <input
                    type="number"
                    className="input-field mt-1"
                    value={transactionAmount}
                    onChange={(e) => setTransactionAmount(e.target.value)}
                    placeholder="0"
                    min="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Description
                  </label>
                  <input
                    type="text"
                    className="input-field mt-1"
                    placeholder="Description du dépôt"
                  />
                </div>
              </div>
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => setShowDepositModal(false)}
                  className="btn-secondary"
                >
                  Annuler
                </button>
                <button
                  onClick={handleDeposit}
                  className="btn-primary"
                  disabled={!transactionAmount || parseFloat(transactionAmount) <= 0}
                >
                  Déposer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Withdraw Modal */}
      {showWithdrawModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Retrait du compte {selectedAccount?.account_number}
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Montant (HTG)
                  </label>
                  <input
                    type="number"
                    className="input-field mt-1"
                    value={transactionAmount}
                    onChange={(e) => setTransactionAmount(e.target.value)}
                    placeholder="0"
                    min="0"
                    max={selectedAccount?.balance}
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    Solde disponible: {(selectedAccount?.balance || 0).toLocaleString('fr-FR')} HTG
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Description
                  </label>
                  <input
                    type="text"
                    className="input-field mt-1"
                    placeholder="Description du retrait"
                  />
                </div>
              </div>
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => setShowWithdrawModal(false)}
                  className="btn-secondary"
                >
                  Annuler
                </button>
                <button
                  onClick={handleWithdraw}
                  className="btn-primary"
                  disabled={!transactionAmount || parseFloat(transactionAmount) <= 0}
                >
                  Retirer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SavingsAccounts;